import { createContext, useContext, type Accessor, type JSX } from "solid-js";
import type { Me } from "../api/domain.ts";

/**
 * Who is signed in and in which world, as the shell resolved it from `/me`.
 *
 * Pages read this to hide what the server would refuse anyway: an admin sees
 * every control, a customer user or a previewing admin sees a read-only world.
 * The server stays the authority; this only spares the reader a 403.
 */
export type Session = {
  /** The resolved caller, or undefined while the shell is still asking. */
  me: Accessor<Me | undefined>;
  /** Whether the caller may change anything. */
  canWrite: Accessor<boolean>;
  /** Whether the caller sees one customer's world, as a member or in preview. */
  isCustomer: Accessor<boolean>;
  /** Starts previewing a customer; the shell reloads into that world. */
  enterPreview: (customer: string) => void;
  /** Ends a running preview. */
  exitPreview: () => void;
};

const SessionContext = createContext<Session>();

/**
 * What a page sees when no shell provides a session, as in a component test:
 * an admin with every power, so existing screens render unchanged.
 */
export const adminSession: Session = {
  me: () => ({ role: "admin", preview: false, bootstrap: true }),
  canWrite: () => true,
  isCustomer: () => false,
  enterPreview: () => undefined,
  exitPreview: () => undefined,
};

export type SessionProviderProps = {
  value: Session;
  children: JSX.Element;
};

export const SessionProvider = (props: SessionProviderProps) => (
  <SessionContext.Provider value={props.value}>{props.children}</SessionContext.Provider>
);

export const useSession = (): Session => useContext(SessionContext) ?? adminSession;
